import { AxiosInstance } from 'axios';
import { GroupReferral, ReferralSearchDirectory } from 'referral-types';
import { addFormValues, simulate } from './utils';
import {
  type CompleteFormFields,
  type CompleteFormFieldsAdmin,
} from '../components/group-referral/form/group-referral.schema';

export const baseEndpoint = 'group-referral';

export class GroupReferralApi {
  private apiInstance: AxiosInstance;

  constructor(apiInstance: AxiosInstance) {
    this.apiInstance = apiInstance;
  }

  async getGroupReferral(id: string): Promise<GroupReferral> {
    await simulate();
    const response = await this.apiInstance.get(`/${id}`);

    return response.data;
  }

  async getSiteGroupReferral(id: string): Promise<GroupReferral> {
    await simulate();
    const response = await this.apiInstance.get(`/site/${id}`);

    return response.data;
  }

  async getGroupReferralsForPage(pageId: string): Promise<Array<GroupReferral>> {
    await simulate();
    const response = await this.apiInstance.get(`/page/${pageId}`);

    return response.data;
  }

  async getRandomGroupReferralsSponsorsByReferralId(referralId: string): Promise<Array<GroupReferral>> {
    await simulate();
    const response = await this.apiInstance.get(`/referral/${referralId}/random-sponsors`);

    return response.data;
  }

  async searchGroupReferrals(searchDirectory: ReferralSearchDirectory): Promise<Array<GroupReferral>> {
    await simulate();
    const response = await this.apiInstance.post(`/search`, searchDirectory);

    return response.data;
  }

  async createGroupReferral(groupId: string, formValues: CompleteFormFieldsAdmin): Promise<GroupReferral> {
    await simulate();
    const formData = new FormData();

    addFormValues(formValues, formData);

    if (formValues.expirationDateTime === '') {
      formData.delete('expirationDateTime');
    }

    const response = await this.apiInstance.post(`/group/${groupId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  }

  async updateGroupReferralAndReferral(
    groupReferralId: string,
    formValues: Partial<CompleteFormFields>
  ): Promise<GroupReferral> {
    await simulate();
    const formData = new FormData();

    addFormValues(formValues, formData);

    const response = await this.apiInstance.put(`/${groupReferralId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  }

  async createGroupReferralFromSponsoredOrSiteReferral(
    groupId: string,
    referralId: string,
    formValues: Pick<CompleteFormFieldsAdmin, 'position' | 'isSearchable' | 'referralLink' | 'referralCode' | 'slug'>
  ): Promise<GroupReferral> {
    await simulate();
    const formData = new FormData();

    addFormValues(formValues, formData);

    const response = await this.apiInstance.post(`/group/${groupId}/referral/${referralId}`, formData);

    return response.data;
  }

  async updateGroupReferral(groupReferralId: string, payload: Partial<CompleteFormFields>): Promise<GroupReferral> {
    await simulate();
    const formData = new FormData();

    addFormValues(payload, formData);

    const response = await this.apiInstance.put(`/${groupReferralId}`, formData);

    return response.data;
  }

  async deleteGroupReferral(groupId: string): Promise<GroupReferral> {
    await simulate();
    const response = await this.apiInstance.delete(`/${groupId}`);

    return response.data;
  }

  async reorderGroupReferralsForGroup(groupId: string, groupReferralIds: string[]): Promise<Array<GroupReferral>> {
    await simulate();
    const response = await this.apiInstance.put(`/group/${groupId}/reorder`, { groupReferralIds });

    return response.data;
  }
}
